import React, { useEffect, useState, useCallback } from 'react';
import {
    Box,
    Card,
    CardMedia,
    CardContent,
    Typography,
    Grid,
    IconButton,
    Dialog,
    DialogContent,
    DialogTitle,
    DialogActions,
    DialogContentText,
    useMediaQuery,
    CircularProgress,
    Chip,
    Button,
    Alert,
    Tooltip,
    TextField,
    InputAdornment
} from '@mui/material';
import PageWithTitle from "../../../components/pages/pageWithTitle";
import localStrings from "../../../localStrings";
import useAuth from "../../../hooks/useAuth";
import { executeQueryUtil } from "../../../utils/gqlUtil";
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import moment from 'moment';
import 'moment/locale/fr';
import enqueueSnackbarWithSound from "../../../utils/SnackBarWithSound";
import { toast } from "react-hot-toast";

moment.locale('fr');

function IAImages() {
    const lgUp = useMediaQuery((theme) => theme.breakpoints.up("lg"));
    const { currentBrand, currentEstablishment, userInDb } = useAuth();
    const [images, setImages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedImage, setSelectedImage] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [filteredImages, setFilteredImages] = useState([]);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [imageToDelete, setImageToDelete] = useState(null);

    // Mémoriser les IDs pour éviter les re-renders
    const brandId = currentBrand()?.id;
    const establishmentId = currentEstablishment()?.id;

    const loadImages = useCallback(async () => {
        setLoading(true);
        try {
            // Appeler le service Firebase pour récupérer les images
            const response = await fetch(
                `https://us-central1-foodreportapp-8c2f0.cloudfunctions.net/flowiseImageService/listImages`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        brandId: brandId,
                        establishmentId: establishmentId
                    })
                }
            );

            if (response.ok) {
                const data = await response.json();
                // Trier par date de création décroissante (plus récent en premier)
                const sortedImages = (data.images || []).sort((a, b) => 
                    new Date(b.timeCreated) - new Date(a.timeCreated)
                );
                setImages(sortedImages);
                setFilteredImages(sortedImages);
            } else {
                console.error('Erreur lors du chargement des images');
                setImages([]);
                setFilteredImages([]);
            }
        } catch (error) {
            console.error('Erreur:', error);
            enqueueSnackbarWithSound(toast, null, null, localStrings.error || 'Erreur lors du chargement des images', 'error');
            setImages([]);
            setFilteredImages([]);
        } finally {
            setLoading(false);
        }
    }, [brandId, establishmentId]);

    // Charger les images au montage et quand les IDs changent
    useEffect(() => {
        if (brandId && establishmentId) {
            loadImages();
        }
    }, [brandId, establishmentId, loadImages]);

    // Filtrer les images selon le terme de recherche
    useEffect(() => {
        if (!searchTerm.trim()) {
            setFilteredImages(images);
        } else {
            const filtered = images.filter(image => 
                image.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                image.metadata?.originalUrl?.toLowerCase().includes(searchTerm.toLowerCase())
            );
            setFilteredImages(filtered);
        }
    }, [images, searchTerm]);

    const handleDownload = async (imageUrl, imageName) => {
        try {
            // Pour les URLs Firebase Storage, on peut directement ouvrir dans un nouvel onglet
            // pour déclencher le téléchargement
            if (imageUrl.includes('firebasestorage.googleapis.com')) {
                // Ajouter le paramètre de téléchargement à l'URL
                const downloadUrl = imageUrl.includes('?') 
                    ? `${imageUrl}&alt=media`
                    : `${imageUrl}?alt=media`;
                
                // Créer un lien temporaire et simuler un clic
                const link = document.createElement('a');
                link.href = downloadUrl;
                link.download = imageName || 'image.png';
                link.target = '_blank';
                link.rel = 'noopener noreferrer';
                
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                
                enqueueSnackbarWithSound(toast, null, null, localStrings.notif.imageDownloadedSuccessfully || 'Image téléchargée avec succès', 'success');
            } else {
                // Fallback pour les autres URLs
                const response = await fetch(imageUrl, {
                    mode: 'cors',
                    credentials: 'omit'
                });
                
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = imageName || 'image.png';
                link.style.display = 'none';
                
                document.body.appendChild(link);
                link.click();
                
                // Nettoyer après un délai
                setTimeout(() => {
                    document.body.removeChild(link);
                    window.URL.revokeObjectURL(url);
                }, 100);
                
                enqueueSnackbarWithSound(toast, null, null, localStrings.notif.imageDownloadedSuccessfully || 'Image téléchargée avec succès', 'success');
            }
        } catch (error) {
            console.error('Erreur lors du téléchargement:', error);
            
            // Si le téléchargement échoue, essayer d'ouvrir dans un nouvel onglet
            try {
                window.open(imageUrl, '_blank');
                enqueueSnackbarWithSound(toast, null, null, localStrings.notif.imageOpenedInNewTab || 'Image ouverte dans un nouvel onglet', 'info');
            } catch (e) {
                enqueueSnackbarWithSound(toast, null, null, localStrings.error || 'Erreur lors du téléchargement', 'error');
            }
        }
    };

    const handleDeleteClick = (imagePath) => {
        setImageToDelete(imagePath);
        setDeleteDialogOpen(true);
    };

    const handleDeleteCancel = () => {
        setDeleteDialogOpen(false);
        setImageToDelete(null);
    };

    const handleDeleteConfirm = async () => {
        if (!imageToDelete) return;

        try {
            const response = await fetch(
                `https://us-central1-foodreportapp-8c2f0.cloudfunctions.net/flowiseImageService/deleteImage`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        imagePath: imageToDelete,
                        brandId: brandId,
                        establishmentId: establishmentId
                    })
                }
            );

            if (response.ok) {
                enqueueSnackbarWithSound(toast, null, null, localStrings.notif.imageDeleted || 'Image supprimée avec succès', 'success');
                loadImages(); // Recharger la liste
                setDeleteDialogOpen(false);
                setImageToDelete(null);
            } else {
                throw new Error('Erreur lors de la suppression');
            }
        } catch (error) {
            console.error('Erreur:', error);
            enqueueSnackbarWithSound(toast, null, null, localStrings.error || 'Erreur lors de la suppression', 'error');
        }
    };

    const formatFileSize = (bytes) => {
        if (!bytes) return 'N/A';
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        if (bytes === 0) return '0 Bytes';
        const i = parseInt(Math.floor(Math.log(bytes) / Math.log(1024)));
        return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
    };

    const getImageType = (metadata) => {
        if (metadata?.originalUrl?.includes('flowise')) {
            return { label: 'Flowise', color: 'primary' };
        }
        if (metadata?.source === 'download') {
            return { label: localStrings.downloaded || 'Téléchargée', color: 'secondary' };
        }
        return { label: localStrings.aiChat || 'IA', color: 'success' };
    };

    if (!currentEstablishment() || !currentBrand()) {
        return (
            <PageWithTitle title={localStrings.aiImages || "Images IA"}>
                <Alert severity="warning">
                    {localStrings.selectBrandAndEstablishment || "Veuillez sélectionner une marque et un établissement"}
                </Alert>
            </PageWithTitle>
        );
    }

    return (
        <PageWithTitle 
            title={localStrings.aiGeneratedImages || "Images générées par l'IA"}
            mainButtons={
                <Button
                    variant="contained"
                    startIcon={<RefreshIcon />}
                    onClick={loadImages}
                    disabled={loading}
                >
                    {localStrings.refresh || "Actualiser"}
                </Button>
            }
        >
            {/* Barre de recherche commentée pour le moment */}
            {/* <Box sx={{ mb: 3 }}>
                <TextField
                    fullWidth
                    variant="outlined"
                    placeholder={localStrings.searchImages || "Rechercher des images..."}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon />
                            </InputAdornment>
                        ),
                    }}
                />
            </Box> */}

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 5, mt: 3 }}>
                    <CircularProgress />
                </Box>
            ) : filteredImages.length === 0 ? (
                <Alert severity="info" sx={{ mt: 3 }}>
                    {searchTerm 
                        ? `${localStrings.noImagesFoundFor || 'Aucune image trouvée pour'} "${searchTerm}"`
                        : localStrings.noImagesGenerated || 'Aucune image générée pour le moment'
                    }
                </Alert>
            ) : (
                <Grid container spacing={2} sx={{ mt: 2 }}>
                    {filteredImages.map((image, index) => {
                        const imageType = getImageType(image.metadata);
                        return (
                            <Grid item xs={12} sm={lgUp ? 6 : 12} md={lgUp ? 6 : 12} lg={6} key={index}>
                                <Card 
                                    sx={{ 
                                        height: '100%',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        '&:hover': {
                                            boxShadow: 6,
                                            transform: 'translateY(-2px)',
                                            transition: 'all 0.3s'
                                        }
                                    }}
                                >
                                    <Box sx={{ position: 'relative' }}>
                                        <CardMedia
                                            component="img"
                                            height="200"
                                            image={image.publicUrl}
                                            alt={image.name}
                                            sx={{ 
                                                cursor: 'pointer',
                                                objectFit: 'cover'
                                            }}
                                            onClick={() => setSelectedImage(image)}
                                        />
                                        {/* Chip retiré 
                                        <Chip
                                            label={imageType.label}
                                            color={imageType.color}
                                            size="small"
                                            sx={{
                                                position: 'absolute',
                                                top: 8,
                                                right: 8,
                                            }}
                                        /> */}
                                        <IconButton
                                            sx={{
                                                position: 'absolute',
                                                bottom: 8,
                                                right: 8,
                                                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                                '&:hover': {
                                                    backgroundColor: 'rgba(255, 255, 255, 1)',
                                                }
                                            }}
                                            size="small"
                                            onClick={() => setSelectedImage(image)}
                                        >
                                            <ZoomInIcon />
                                        </IconButton>
                                    </Box>
                                    <CardContent sx={{ flexGrow: 1, py: 1 }}>
                                        <Typography variant="caption" color="text.secondary" gutterBottom>
                                            {moment(image.timeCreated).format('DD/MM/YYYY HH:mm')}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                                            {formatFileSize(image.size)}
                                        </Typography>
                                        <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                                            <Tooltip title={localStrings.download || "Télécharger"}>
                                                <IconButton 
                                                    size="small"
                                                    onClick={() => handleDownload(image.publicUrl, image.name)}
                                                >
                                                    <DownloadIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title={localStrings.delete || "Supprimer"}>
                                                <IconButton 
                                                    size="small"
                                                    color="error"
                                                    onClick={() => handleDeleteClick(image.name)}
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>
                        );
                    })}
                </Grid>
            )}

            {/* Dialog pour afficher l'image en grand */}
            <Dialog
                open={!!selectedImage}
                onClose={() => setSelectedImage(null)}
                maxWidth="lg"
                fullWidth
            >
                <DialogContent sx={{ position: 'relative', p: 0 }}>
                    <IconButton
                        onClick={() => setSelectedImage(null)}
                        sx={{
                            position: 'absolute',
                            right: 8,
                            top: 8,
                            backgroundColor: 'rgba(255, 255, 255, 0.9)',
                            zIndex: 1,
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                    {selectedImage && (
                        <>
                            <img
                                src={selectedImage.publicUrl}
                                alt={selectedImage.name}
                                style={{
                                    width: '100%',
                                    height: 'auto',
                                    display: 'block'
                                }}
                            />
                            <Box sx={{ p: 2, backgroundColor: 'background.paper' }}>
                                <Typography variant="body2" color="text.secondary">
                                    {localStrings.date || "Date"}: {moment(selectedImage.timeCreated).format('DD MMMM YYYY à HH:mm')}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {localStrings.size || "Taille"}: {formatFileSize(selectedImage.size)}
                                </Typography>
                            </Box>
                        </>
                    )}
                </DialogContent>
            </Dialog>

            {/* Dialog de confirmation de suppression */}
            <Dialog
                open={deleteDialogOpen}
                onClose={handleDeleteCancel}
                aria-labelledby="alert-dialog-slide-title"
                aria-describedby="alert-dialog-slide-description"
            >
                <DialogTitle id="alert-dialog-slide-title">
                    {localStrings.confirmMessage.confirmDelete || "Confirmer la suppression"}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-slide-description">
                        {localStrings.confirmMessage.deleteImage || 'Êtes-vous sûr de vouloir supprimer cette image ?'}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDeleteCancel} color="primary">
                        {localStrings.cancel || "Annuler"}
                    </Button>
                    <Button onClick={handleDeleteConfirm} color="primary">
                        {localStrings.confirm || "Confirmer"}
                    </Button>
                </DialogActions>
            </Dialog>
        </PageWithTitle>
    );
}

export default IAImages;